import React from "react";
import { FiX } from "react-icons/fi";
import { useDownload } from "../../pages/DownloadWindow/DownloadContext";
import { trackDownloadClick } from "../../lib/analytics";
import firstImage from "./Assets/firstImageWindows.png";
import secondImage from "./Assets/secondImageWindows.png";
import thirdImage from "./Assets/thirdImageWindows.png";

const WINDOWS_STEPS = [
  {
    image: firstImage,
    title: "1. Open",
    description: "Open ZeroTypingAI.msi from your downloads.",
  },
  {
    image: secondImage,
    title: "2. Install the App",
    description:
      "Launch the installer and follow the setup wizard to install Zero Typing AI.",
  },
  {
    image: thirdImage,
    title: "3. Launch Zero Typing AI",
    description:
      "Open Zero Typing AI from your taskbar whenever you need it.",
  },
];

const WINDOWS_DOWNLOAD_URL =
  "https://zerotyping.ai/ztaupdate/ZeroTypingAI.msi";
const WINDOWS_DOWNLOAD_FILENAME = "ZeroTypingAI.msi";

const WindowsInstallModal = ({ isOpen, setIsOpen, onDownloadManually }) => {
  // Live state of the streaming download (see DownloadContext.startDownload).
  // Null outside the provider - the modal then shows its static copy.
  const download = useDownload();

  if (!isOpen) return null;

  const onClose = () => setIsOpen(false);

  const handleManualDownloadClick = () => {
    trackDownloadClick({
      platform: "windows",
      file_name: WINDOWS_DOWNLOAD_FILENAME,
      location: "install_modal_manual_link",
    });
    onDownloadManually?.();
  };

  const isDownloading = Boolean(download?.isDownloading);
  const isComplete = Boolean(download?.isComplete);
  const failed = Boolean(download?.error) && !isDownloading;
  const percent = Math.min(100, Math.round(download?.progress || 0));
  const sizeText = download?.totalMB
    ? `${download.downloadedMB} MB of ${download.totalMB} MB`
    : `${download?.downloadedMB || "0.0"} MB`;

  return (
    <div
      className="fixed inset-0 z-[200] bg-[#0000007a] backdrop-blur-[2px] overflow-y-auto font-inter"
      style={{
        animation: "installBackdropIn 260ms ease-out both",
      }}
    >
      <style>{`
        @keyframes installBackdropIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes installModalIn {
          from { opacity: 0; transform: translateY(14px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0)    scale(1);    }
        }
        @keyframes installHeaderIn {
          from { opacity: 0; transform: translateY(-6px); }
          to   { opacity: 1; transform: translateY(0);    }
        }
        @keyframes installCardIn {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: translateY(0);    }
        }
        @keyframes installCardBlink {
          0%, 100% {
            opacity: 0.86;
            transform: scale(1);
            filter: drop-shadow(0 0 0 transparent);
          }
          50% {
            opacity: 1;
            transform: scale(1.04);
            filter: drop-shadow(0 6px 18px rgba(64, 123, 255, 0.32));
          }
        }
        @keyframes installShimmer {
          0%   { transform: translateX(-220%) skewX(-15deg); opacity: 0; }
          15%  { opacity: 1; }
          85%  { opacity: 1; }
          100% { transform: translateX(380%) skewX(-15deg);  opacity: 0; }
        }
        @keyframes installLinkPulse {
          0%, 100% { color: #0F1115; }
          50%      { color: #407BFF; }
        }
        @keyframes installStepNumPulse {
          0%, 100% { color: #0F1115; transform: scale(1);    }
          50%      { color: #407BFF; transform: scale(1.08); }
        }
        @keyframes installDotFloat {
          0%, 100% { transform: translateY(0)     scale(1);   opacity: 0.6; }
          50%      { transform: translateY(-2px) scale(1.25); opacity: 1;   }
        }
        .install-card-wrap {
          transition: transform 280ms cubic-bezier(0.33, 1, 0.68, 1),
                      box-shadow 280ms ease;
        }
        .install-card-wrap:hover {
          transform: translateY(-3px);
        }
        .install-card-wrap:hover .install-card-image {
          box-shadow: 0 14px 32px -14px rgba(64, 123, 255, 0.28);
        }
        .install-close-btn {
          transition: transform 280ms cubic-bezier(0.33, 1, 0.68, 1),
                      color 200ms ease,
                      background-color 200ms ease;
        }
        .install-close-btn:hover {
          transform: rotate(90deg);
          background-color: #F2F3F6;
        }
        .install-card-image {
          transition: box-shadow 280ms ease;
          position: relative;
        }
        .install-shimmer {
          position: absolute;
          inset: 0 auto 0 0;
          width: 45%;
          pointer-events: none;
          background: linear-gradient(
            90deg,
            transparent 0%,
            rgba(255, 255, 255, 0.55) 50%,
            transparent 100%
          );
          mix-blend-mode: overlay;
          z-index: 2;
        }
        .install-step-num {
          display: inline-block;
          font-weight: 700;
          will-change: transform, color;
        }
        .install-link-pulse {
          animation: installLinkPulse 2.6s ease-in-out infinite;
        }
        .install-dot {
          display: inline-block;
          width: 4px;
          height: 4px;
          border-radius: 9999px;
          background: #407BFF;
          margin-left: 6px;
          vertical-align: middle;
          animation: installDotFloat 1.4s ease-in-out infinite;
        }
      `}</style>
      <div className="min-h-full flex items-center justify-center p-3 sm:p-4 md:p-6">
        <div
          className="bg-white rounded-2xl sm:rounded-3xl border border-[#E5E7EB] w-full max-w-[1100px] p-4 sm:p-5 md:p-7 shadow-2xl"
          style={{
            animation:
              "installModalIn 420ms cubic-bezier(0.16, 1, 0.3, 1) both",
          }}
        >
          <div
            className="flex items-start justify-between gap-3 mb-1 sm:mb-1.5"
            style={{
              animation:
                "installHeaderIn 320ms cubic-bezier(0.16, 1, 0.3, 1) 120ms both",
            }}
          >
            <h2 className="text-sm sm:text-base md:text-lg font-semibold text-[#0F1115] leading-tight">
              1. Install and open the app
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="install-close-btn shrink-0 text-[#5C6370] hover:text-[#0F1115] rounded-full p-1 -mr-0.5"
              aria-label="Close"
            >
              <FiX className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
          </div>

          {isDownloading && (
            <div
              className="mb-3 sm:mb-4"
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={percent}
              aria-label="Downloading ZeroTypingAI.msi"
              style={{
                animation:
                  "installHeaderIn 320ms cubic-bezier(0.16, 1, 0.3, 1) 200ms both",
              }}
            >
              <div className="flex items-center justify-between text-xs sm:text-sm text-[#0F1115] mb-1.5">
                <span className="font-medium">
                  Downloading {WINDOWS_DOWNLOAD_FILENAME}…
                </span>
                <span className="text-[#5C6370] tabular-nums">
                  {download?.totalMB ? `${percent}% · ` : ""}
                  {sizeText}
                </span>
              </div>
              <div className="h-2 w-full rounded-full bg-[#E5E7EB] overflow-hidden">
                <div
                  className="h-full rounded-full transition-[width] duration-200"
                  style={{
                    width: download?.totalMB ? `${percent}%` : "35%",
                    background:
                      "linear-gradient(90deg, #3AB1F6 0%, #407BFF 100%)",
                  }}
                />
              </div>
            </div>
          )}

          <p
            className="text-xs sm:text-sm md:text-[15px] text-[#5C6370] mb-4 sm:mb-5 md:mb-6 leading-relaxed"
            style={{
              animation:
                "installHeaderIn 320ms cubic-bezier(0.16, 1, 0.3, 1) 200ms both",
            }}
          >
            {isDownloading
              ? "Keep this tab open while the installer downloads. Once it finishes, follow the steps below. Having trouble? You can "
              : isComplete
                ? "Download complete. Follow the steps below. Need the file again? You can "
                : failed
                  ? "The automatic download couldn't start, so your browser is downloading it directly. If nothing appears, you can "
                  : "The desktop app should have downloaded automatically. If not, you can "}
            <a
              href={WINDOWS_DOWNLOAD_URL}
              download={WINDOWS_DOWNLOAD_FILENAME}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleManualDownloadClick}
              className="install-link-pulse font-medium hover:text-[#407BFF] whitespace-nowrap"
              style={{
                backgroundImage:
                  "linear-gradient(currentColor, currentColor)",
                backgroundPosition: "0 100%",
                backgroundRepeat: "no-repeat",
                backgroundSize: "100% 1px",
                paddingBottom: "1px",
              }}
            >
              download manually.
            </a>
            <span className="install-dot" aria-hidden="true" />
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-3 md:gap-4">
            {WINDOWS_STEPS.map((step, i) => {
              const titleMatch = step.title.match(/^(\d+\.)\s+(.*)$/);
              const stepNum = titleMatch ? titleMatch[1] : "";
              const stepRest = titleMatch ? titleMatch[2] : step.title;
              return (
                <div
                  key={i}
                  className="install-card-wrap flex flex-col gap-2.5 sm:gap-3 min-w-0"
                  style={{
                    animation: `installCardIn 460ms cubic-bezier(0.16, 1, 0.3, 1) ${
                      280 + i * 110
                    }ms both`,
                  }}
                >
                  <div className="install-card-image bg-[#F2F3F6] rounded-xl sm:rounded-2xl aspect-[16/9] sm:aspect-[5/4] flex items-center justify-center overflow-hidden">
                    <img
                      src={step.image}
                      alt={step.title}
                      className="w-full h-full object-contain"
                      draggable={false}
                      style={{
                        animation: `installCardBlink 2.4s ease-in-out ${
                          i * 0.4
                        }s infinite`,
                        willChange: "opacity, transform, filter",
                      }}
                    />
                    <span
                      className="install-shimmer"
                      style={{
                        animation: `installShimmer 3.6s ease-in-out ${
                          i * 0.9 + 0.6
                        }s infinite`,
                      }}
                      aria-hidden="true"
                    />
                  </div>
                  <div className="px-0.5 min-w-0">
                    <h3 className="font-semibold text-[#0F1115] text-sm sm:text-[15px] md:text-base mb-0.5 sm:mb-1">
                      {stepNum && (
                        <span
                          className="install-step-num mr-1"
                          style={{
                            animation: `installStepNumPulse 2.6s ease-in-out ${
                              i * 0.5
                            }s infinite`,
                          }}
                        >
                          {stepNum}
                        </span>
                      )}
                      {stepRest}
                    </h3>
                    <p className="text-xs sm:text-[13px] md:text-sm text-[#5C6370] leading-relaxed break-words">
                      {step.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WindowsInstallModal;
